'use strict';

import { useEffect, useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';

import { get } from 'lodash';
import dayjs from 'dayjs';
import { decode } from 'he';
import { fetchFeaturedMedia, stripTags } from '../../../../../lib/util';
import 'cross-fetch/polyfill';
import Layout from '../../../../../lib/components/layout';
import RelatedPosts from '../../../../../lib/components/blog/related-posts';

import SocialLinks from '../../../../../lib/components/blog/single-post/social-links';
import RecentJobs from '../../../../../lib/components/blog/blog-home/recent-jobs';
import RequestProposalForm from '../../../../../lib/components/forms/request-proposal-form';
import { selectPostCat } from '../../../../../lib/components/blog/blog-util';

const SinglePost = ({ post }) => {
  const [postContent] = useState({ __html: post.content.rendered });
  const pageTitle = `${decode(post.title.rendered)} - College Recruiter`;
  const pageDescription = stripTags(decode(post.excerpt.rendered));

  useEffect(() => {
    document.body.setAttribute('id', 'page-top');
    document.body.classList.add('blog');
  }, []);

  const img = get(post, "_embedded['wp:featuredmedia'][0]", null);
  const imgCaption = img ? stripTags(img.caption.rendered) : null;

  const category = selectPostCat(post);
  const author = get(post, '_embedded.author[0]', null);
  const authorAvatarUrl = author && !post.guest_author ? get(author, 'avatar_urls[48]') : null;
  const authorName = post.guest_author ? post.guest_author : author.name;
  return (
    <>
      <Head>
        <meta property="og:type" content="article" />
        <meta property="og:url" content={post.link} />
        <meta property="og:image" content={img.source_url} />
        <meta property="og:pubDate" content={post.date} />
        <meta property="og:modDate" content={post.modified} />
      </Head>

      <Layout pageTitle={pageTitle} pageDescription={pageDescription}>
        <main id="site-main" className="angled-bg top-100">
          <article className="article-single">
            <div className="container-xl pt-5">
              <div className="row justify-content-center pt-5">
                <div className="col-lg-8">
                  {category && <p className="mb-3 fs-5 text-white">{category.name}</p>}
                  <h1 className="text-white">{decode(post.title.rendered)}</h1>
                </div>
              </div>
            </div>

            <div className="angled-bg-bottom pb-4">
              <div className="container-xl py-5">
                <div className="row justify-content-center">
                  <div
                    className={`col-lg-8${post.hasFeaturedMedia ? '' : ` no-featured-image-post`}`}
                  >
                    {img && post.hasFeaturedMedia && (
                      <Image
                        className="w-100 mb-3 rounded article-featured-image"
                        width={856}
                        height={565}
                        src={img.source_url}
                        alt={imgCaption}
                      />
                    )}
                    <div className="row align-items-center justify-content-between">
                      <div className="col-sm-auto py-3">
                        {author && (
                          <div className="author">
                            {authorAvatarUrl && (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={authorAvatarUrl}
                                width={40}
                                height={40}
                                alt={`${authorName} Avatar`}
                              />
                            )}

                            <small className="author-name">{authorName}</small>
                          </div>
                        )}
                      </div>
                      <div className="col-sm-auto py-4">
                        <small className="date">{dayjs(post.date).format('MMMM D, YYYY')}</small>
                      </div>
                    </div>
                    <hr className="my-0" />
                    <div className="row align-items-center justify-content-between">
                      <div className="col-sm-auto py-3">
                        <span className="share">Share</span>
                      </div>
                      <div className="col-sm-auto py-4">
                        <SocialLinks post={post} />
                      </div>
                    </div>
                    <hr className="mt-0 mb-5" />
                    <div dangerouslySetInnerHTML={postContent}></div>
                    <div className="p-4 my-5 background-gray rounded">
                      <p className="mb-3">New Job Postings</p>
                      <RecentJobs number={2} />
                      <Link href={`/job-search`} passHref>
                        <a className="text-btn" href={`/job-search`}>
                          Advanced Search
                        </a>
                      </Link>
                    </div>

                    {post.categories.includes(23422) && (
                      <RequestProposalForm layoutClasses="p-4 my-5 mb-5"></RequestProposalForm>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </article>

          <div className="py-5 background-red-gradient">
            <div className="container-xl">
              <p className="mb-3 fs-5 text-white">
                <strong>Related Articles</strong>
              </p>
              <RelatedPosts post={post} />
              <Link href={`/blog`} passHref>
                <a className="text-btn" href={`/blog`}>
                  View More Articles
                </a>
              </Link>
            </div>
          </div>
        </main>
      </Layout>
    </>
  );
};

export async function getServerSideProps(context) {
  const { slug } = context.params;
  const url = `${process.env.NEXT_PUBLIC_BLOG_API}/posts?slug=${slug}&_embed&_fields=id,_links,author,title,link,content,excerpt,categories,featured_media,date,modified,meta,guest_author`;
  const response = await fetch(url);
  const data = await response.json();

  if (response.status === 404 || !data.length) {
    // return 404 ....
    return { notFound: true, props: { status: 404 } };
  }

  const singlePost = data[0];
  // handle case where featured image may not exist from api
  // Handle when featured image on first post is not present
  const { hasFeaturedMedia, img } = fetchFeaturedMedia(singlePost);

  singlePost.hasFeaturedMedia = hasFeaturedMedia;

  if (!hasFeaturedMedia) {
    singlePost._embedded['wp:featuredmedia'] = [img];
  }

  // setup a cache

  const { res } = context;
  res.setHeader('Cache-Control', 'public, s-maxage=86400, must-revalidate');

  return {
    props: {
      post: singlePost
    }
  };
}

export default SinglePost;
